import { Card, CardBody, Flex, Text } from '@backstage/ui';
import {
  CATEGORY_STATUS,
  type TeamCategorySummaryDto,
  type TeamProjectDto,
} from '@internal/scorecards-common';
import { useMemo, type ReactNode } from 'react';

import { severityDisplay } from '../../severity';
import { useSeverities } from '../../hooks/useSeverities';
import styles from './KpiRow.module.css';

const belowTargetColor = (n: number) =>
  n > 0 ? 'var(--zf-sc-fail)' : 'var(--zf-sc-pass)';

interface KpiCardProps {
  label: string;
  value: ReactNode;
  unit?: string;
  caption?: string;
  valueColor?: string;
}

const KpiCard = ({ label, value, unit, caption, valueColor }: KpiCardProps) => (
  <Card>
    <CardBody>
      <Flex direction="column" gap="2">
        <Text
          as="p"
          variant="body-small"
          weight="bold"
          className={styles.kpiLabel}
        >
          {label}
        </Text>
        <Text as="p" className={styles.kpiValue}>
          <Text as="span" variant="title-medium" style={{ color: valueColor }}>
            {value}
          </Text>
          {unit && (
            <Text as="span" variant="body-large" color="secondary">
              {' '}
              {unit}
            </Text>
          )}
        </Text>
        {caption && (
          <Text
            as="p"
            variant="body-small"
            color="secondary"
            className={styles.kpiCaption}
          >
            {caption}
          </Text>
        )}
      </Flex>
    </CardBody>
  </Card>
);

function useFailingProjectStats(
  projects: TeamProjectDto[],
  severities: ReturnType<typeof useSeverities>['severities'],
) {
  return useMemo(() => {
    const counts: Record<string, number> = {};

    let failingTotal = 0;
    for (const p of projects) {
      if (p.failingChecks.length === 0) continue;
      failingTotal++;
      // failingChecks are ordered as per their severities from the backend; first is worst
      const worst = p.failingChecks[0].severity;
      counts[worst] = (counts[worst] ?? 0) + 1;
    }

    const orderedSeverities = [...severities].sort((a, b) => a.rank - b.rank);
    const topSev = orderedSeverities.find(sv => (counts[sv.id] ?? 0) > 0);
    const caption = topSev
      ? `${counts[topSev.id]} of them with a ${
          severityDisplay(topSev).label
        } failure`
      : undefined;

    return { failingTotal, caption };
  }, [projects, severities]);
}

interface KpiRowProps {
  categories: TeamCategorySummaryDto[];
  projects: TeamProjectDto[];
}

export const KpiRow = ({ categories, projects }: KpiRowProps) => {
  const { severities } = useSeverities();
  const belowTarget = categories.filter(
    c => c.status === CATEGORY_STATUS.BELOW_TARGET,
  );
  const categoriesWithTarget = categories.filter(
    c => c.target !== undefined,
  ).length;
  const hasTargets = categoriesWithTarget > 0;
  const projectsFullyPassing = projects.filter(
    p => p.failingChecks.length === 0,
  ).length;
  const { failingTotal, caption: failingCaption } = useFailingProjectStats(
    projects,
    severities,
  );

  return (
    <section aria-label="Summary" className={styles.kpiRow}>
      <KpiCard
        label="Projects fully passing"
        value={projectsFullyPassing}
        unit={`of ${projects.length}`}
        caption="pass every check that applies to them"
        valueColor="var(--zf-sc-pass)"
      />
      <KpiCard
        label="Failing Projects"
        value={failingTotal}
        unit={`of ${projects.length}`}
        valueColor={failingTotal > 0 ? 'var(--zf-sc-fail)' : undefined}
        caption={failingCaption}
      />
      <KpiCard
        label="Below target Categories"
        value={hasTargets ? belowTarget.length : '—'}
        unit={hasTargets ? `of ${categoriesWithTarget}` : undefined}
        valueColor={
          hasTargets ? belowTargetColor(belowTarget.length) : undefined
        }
        caption={
          hasTargets
            ? belowTarget.map(c => c.name).join(', ') || 'All on target'
            : 'No category targets set'
        }
      />
    </section>
  );
};
